import { last, sortBy } from "lodash";
import { TableColumn } from "PFApp/components/table/table_types";
import Tooltip from "PFComponents/tooltip/tooltip";
import { SkillsFrameworksResponse } from "PFCore/services/admin/skills_frameworks/fetch_skills_frameworks";
import InfoIcon from "PFIcons/icon_info.svg";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getFormattedSkillsFramework } from "../../utils";

export const useSkillsFrameworksListColumns = (skillsFrameworks?: SkillsFrameworksResponse) => {
  const { t } = useTranslation("skillsFrameworks");

  const customFieldsColumns = useMemo(() => {
    const skillFrameworkWithMaxCustomFields = last(
      sortBy(
        (skillsFrameworks?.entries || []).map((framework) => getFormattedSkillsFramework(framework)),
        "customFields.length"
      )
    );

    const customFields = skillFrameworkWithMaxCustomFields?.customFields;
    const columns: TableColumn[] = (customFields || []).map((_, index) => ({
      locked: true,
      orderKey: "customFields",
      name: `customField#${index + 1}`,
      title: t("list.table.headers.customField", { count: index + 1 }),
      size: 2,
      type: "text",
      isDefault: true,
      attribute: ["customFields", String(index), "customType"]
    }));
    return columns;
  }, [skillsFrameworks, t]);

  const columns = useMemo(() => {
    const nameColumn: TableColumn = {
      locked: true,
      orderKey: "name",
      name: "name",
      title: "skillsFrameworks:list.table.headers.name",
      size: 2,
      type: "text",
      isDefault: true,
      attribute: ["name"]
    };

    const descriptionColumn: TableColumn = {
      locked: true,
      orderKey: "description",
      name: "description",
      title: "skillsFrameworks:list.table.headers.description",
      size: 2,
      type: "text",
      isDefault: true,
      attribute: ["description"]
    };

    const additionalColumns: TableColumn[] = [
      {
        orderKey: "skillsCount",
        name: "skillsCount",
        title: "skillsFrameworks:list.table.headers.skillsCount",
        size: 1,
        type: "text",
        attribute: ["skills"]
      },
      {
        orderKey: "profilesCount",
        name: "profilesCount",
        title: "skillsFrameworks:list.table.headers.profilesCount",
        size: 1,
        type: "text",
        attribute: ["activeProfilesCount"]
      },
      {
        name: "insights",
        childrenComponent: (
          <Tooltip content={t("list.table.headers.insightsTooltip")}>
            <span style={{ height: 20, alignSelf: "center" }}>
              <InfoIcon height={20} width={20} />
            </span>
          </Tooltip>
        ),
        title: "skillsFrameworks:list.table.headers.insights",
        size: 2,
        type: "text",
        attribute: ["coverage"]
      }
    ];
    return [nameColumn, descriptionColumn, ...customFieldsColumns, ...additionalColumns];
  }, [customFieldsColumns, t]);

  return { columns };
};
